/**
 * Awards Ballot block: voting form and its states.
 *
 * Markup: template-parts/awards/blocks/ballot.php. The vote radios live in the
 * Nominees block and join this form through their form attribute; choices are
 * read from the radios and their change events are caught on document. After
 * sending, the radios are locked and the saved choices cleared.
 *
 * States (root modifier awards-ballot--{state}, [data-ballot-show] toggles):
 *   empty     nothing chosen, fields disabled
 *   partial   some public categories chosen
 *   complete  every public category chosen
 *   sent      "Kolla din inkorg", waiting for the email confirmation
 *   closed    the deadline passed while the page was open
 * Sending is a busy flag on top of partial/complete.
 *
 * API: POST config.ballotUrl, see CF_Awards_REST_Controller::create_ballot().
 */

import createCaptcha from './turnstile';
import { END_EVENT } from './countdown';

// Matches CF_Awards_Mailer::RESEND_COOLDOWN.
const RESEND_COOLDOWN = 60;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VOTE_NAME = /^votes\[(.+)\]$/;
const STATES = ['empty', 'partial', 'complete', 'sent', 'closed'];
// Nominee card modifier for the ring pulse on a fresh pick (_awards-nominees.scss).
const JUST_PICKED = 'awards-nominee--just';

// REST params to the field showing their error.
const PARAM_FIELDS = { email: 'email', terms: 'terms', age: 'terms' };

export default function initBallot(root) {
  const config = JSON.parse(root.dataset.config);
  const i18n = JSON.parse(root.dataset.i18n);
  const form = root.querySelector('form');
  const fields = form.querySelector('[data-ballot-fields]');
  const email = form.elements.email;
  const terms = form.elements.terms;
  const submit = form.querySelector('[data-ballot-submit]');
  const resend = root.querySelector('[data-ballot-resend]');
  const status = root.querySelector('[data-ballot-status]');
  const resendStatus = root.querySelector('[data-ballot-resend-status]');
  const slots = {
    form: root.querySelector('[data-ballot-captcha="form"]'),
    sent: root.querySelector('[data-ballot-captcha="sent"]'),
  };
  const errors = {};
  const rows = Array.from(root.querySelectorAll('[data-ballot-row]'));
  const storageKey = `cf-awards-votes-${config.edition}`;
  const end = Date.parse(config.votingEnd);
  const captcha = createCaptcha({ siteKey: config.turnstileSiteKey, script: config.turnstileScript });

  let state = 'empty';
  // Choices as of the last render, i.e. before the current click.
  let chosen = {};
  let busy = false;
  let lastPayload = null;
  let cooldownTimer = null;

  root.querySelectorAll('[data-ballot-error]').forEach((element) => {
    errors[element.dataset.ballotError] = element;
  });

  const radios = () => document.querySelectorAll(`input[form="${form.id}"]`);
  const isClosed = () => !Number.isNaN(end) && Date.now() >= end;

  function announce(message) {
    status.textContent = '';
    // A fresh text node after a tick makes screen readers repeat the same message.
    setTimeout(() => {
      status.textContent = message;
    }, 50);
  }

  // Read from the radios, not FormData: it skips them while they are locked.
  function getVotes() {
    const votes = {};

    radios().forEach((radio) => {
      if (radio.checked) {
        votes[radio.dataset.categoryUid] = radio.value;
      }
    });

    return votes;
  }

  function saveVotes(votes) {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(votes));
    } catch (e) {}
  }

  function clearSavedVotes() {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {}
  }

  // Locked while the sent ballot is shown, so the summary matches what was sent.
  function lockChoices(locked) {
    radios().forEach((radio) => {
      radio.disabled = locked;
    });
  }

  // The page is cached, so choices made earlier in this session are restored here.
  function restoreVotes() {
    let votes = {};

    try {
      votes = JSON.parse(sessionStorage.getItem(storageKey)) || {};
    } catch (e) {}

    radios().forEach((radio) => {
      if (votes[radio.dataset.categoryUid] === radio.value) {
        radio.checked = true;
      }
    });
  }

  function render() {
    const votes = getVotes();
    const count = Object.keys(votes).length;

    chosen = votes;

    if (state !== 'sent' && state !== 'closed') {
      if (!count) {
        state = 'empty';
      } else {
        state = count < rows.length ? 'partial' : 'complete';
      }
    }

    STATES.forEach((name) => root.classList.toggle(`awards-ballot--${name}`, name === state));
    root.classList.toggle('awards-ballot--busy', busy);

    root.querySelectorAll('[data-ballot-show]').forEach((element) => {
      element.hidden = !element.dataset.ballotShow.split(' ').includes(state);
    });

    root.querySelector('[data-ballot-count]').textContent = count;

    rows.forEach((row) => {
      const uid = row.dataset.ballotRow;
      const radio = votes[uid]
        ? document.querySelector(`input[form="${form.id}"][data-category-uid="${CSS.escape(uid)}"]:checked`)
        : null;

      row.classList.toggle('is-chosen', Boolean(radio));
      row.querySelector('[data-ballot-nominee]').textContent = radio ? radio.dataset.nomineeName : i18n.noChoice;
      row.querySelector('[data-ballot-pick-label]').textContent = radio ? i18n.change : i18n.choose;
      // After sending only the chosen nominees are listed.
      row.hidden = state === 'sent' && !radio;
    });

    fields.disabled = state === 'empty' || busy;
    submit.disabled = state === 'empty' || busy;
    submit.querySelector('[data-ballot-submit-label]').hidden = busy;
    submit.querySelector('.awards-ballot__busy').hidden = !busy;
  }

  function setError(name, message) {
    const element = errors[name];
    const input = form.elements[name];

    element.querySelector('[data-ballot-error-text]').textContent = message || '';
    element.hidden = !message;

    if (!input) {
      return;
    }

    const describedBy = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== element.id);

    if (message) {
      describedBy.push(element.id);
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }

    if (describedBy.length) {
      input.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  function clearErrors() {
    Object.keys(errors).forEach((name) => setError(name, ''));
  }

  function validate() {
    const invalid = [];

    if (!EMAIL_PATTERN.test(email.value.trim())) {
      setError('email', i18n.emailInvalid);
      invalid.push(email);
    }

    if (!terms.checked) {
      setError('terms', i18n.termsRequired);
      invalid.push(terms);
    }

    if (invalid.length) {
      invalid[0].focus();
    }

    return !invalid.length;
  }

  function close() {
    if (state === 'closed') {
      return;
    }

    state = 'closed';
    busy = false;
    captcha.reset();
    lockChoices(true);
    render();
    announce(i18n.closed);
  }

  // The emailed link is valid for data-link-ttl seconds, but never after the deadline.
  function showLinkValidity() {
    const ttl = Number(root.dataset.linkTtl) * 1000;
    const byDeadline = !Number.isNaN(end) && end - Date.now() < ttl;

    root.querySelectorAll('[data-ballot-link-valid]').forEach((element) => {
      element.hidden = (element.dataset.ballotLinkValid === 'deadline') !== byDeadline;
    });
  }

  function setBusy(value) {
    busy = value;
    render();
  }

  function startCooldown() {
    // Next to the button, not inside it, so the text can wrap on narrow screens.
    const label = root.querySelector('[data-ballot-cooldown]');
    const until = Date.now() + RESEND_COOLDOWN * 1000;

    clearInterval(cooldownTimer);
    resend.disabled = true;

    const tick = () => {
      const left = Math.ceil((until - Date.now()) / 1000);

      if (left <= 0) {
        clearInterval(cooldownTimer);
        resend.disabled = false;
        label.textContent = '';
        return;
      }

      const time = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
      label.textContent = ` (${i18n.cooldown.replace('%s', time)})`;
    };

    tick();
    cooldownTimer = setInterval(tick, 1000);
  }

  // Maps an API error to the place it is shown; `where` is "form" or "sent".
  function showApiError(data, where) {
    const code = data && data.code;
    const message = (data && data.message) || i18n.generic;
    const generic = (text) => {
      if (where === 'sent') {
        resendStatus.textContent = text;
      } else {
        setError('form', text);
      }
    };

    if (code === 'voting_closed') {
      close();
      return;
    }

    if (where === 'form' && (code === 'invalid_email' || code === 'email_not_allowed')) {
      setError('email', message);
      return;
    }

    if (where === 'form' && (code === 'rest_invalid_param' || code === 'rest_missing_callback_param')) {
      const params = (data.data && data.data.params) || {};
      // Invalid params come as an object keyed by name, missing ones as a list of names.
      const names = Array.isArray(params) ? params : Object.keys(params);
      const shown = new Set(names.map((name) => PARAM_FIELDS[name]).filter(Boolean));

      if (shown.has('email')) {
        setError('email', i18n.emailInvalid);
      }

      if (shown.has('terms')) {
        setError('terms', i18n.termsRequired);
      }

      if (shown.size) {
        return;
      }
    }

    if (code === 'captcha_failed') {
      generic(i18n.captcha);
      return;
    }

    generic(message);
  }

  /**
   * Sends the ballot with a fresh Turnstile token.
   *
   * @param {Object} payload Request body without the token.
   * @param {string} where   "form" for the first send, "sent" for a resend.
   * @return {Promise<boolean>} Whether the server accepted it.
   */
  async function send(payload, where) {
    let accepted = false;

    try {
      const token = await captcha.token(slots[where]);
      const response = await fetch(config.ballotUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, turnstile: token }),
      });
      const data = await response.json().catch(() => null);

      if (response.ok) {
        accepted = true;
      } else {
        showApiError(data, where);
      }
    } catch (e) {
      // Turnstile failed to load or render, or the network is down.
      showApiError({ code: e.message.startsWith('Turnstile') ? 'captcha_failed' : '' }, where);
    } finally {
      captcha.reset();
    }

    return accepted;
  }

  async function onSubmit(event) {
    event.preventDefault();

    if (busy || state === 'empty' || state === 'sent' || state === 'closed') {
      return;
    }

    if (isClosed()) {
      close();
      return;
    }

    clearErrors();

    if (!validate()) {
      return;
    }

    lastPayload = {
      edition: config.edition,
      votes: getVotes(),
      email: email.value.trim(),
      terms: true,
      age: true,
      newsletter: form.elements.newsletter.checked,
      cf_hp: form.elements.cf_hp.value,
    };

    setBusy(true);
    announce(i18n.submitting);

    const accepted = await send(lastPayload, 'form');

    if (state === 'closed') {
      return;
    }

    busy = false;

    if (!accepted) {
      render();
      // Only now: the fields were disabled while sending and couldn't take focus.
      // Without a field error, back to the button (it lost focus while disabled).
      (form.querySelector('[aria-invalid="true"]') || submit).focus();

      captcha.prepare(slots.form);
      return;
    }

    // A reload after sending starts with an empty ballot.
    clearSavedVotes();
    lockChoices(true);
    state = 'sent';
    root.querySelectorAll('[data-ballot-email]').forEach((element) => {
      element.textContent = lastPayload.email;
    });
    showLinkValidity();
    resendStatus.textContent = '';
    render();
    startCooldown();
    root.querySelector('[data-ballot-sent-title]').focus();
    announce(i18n.sent);
  }

  async function onResend() {
    if (!lastPayload || resend.disabled) {
      return;
    }

    if (isClosed()) {
      close();
      return;
    }

    resend.disabled = true;
    resendStatus.textContent = '';

    if (await send(lastPayload, 'sent')) {
      resendStatus.textContent = i18n.resent;
      startCooldown();
    } else if (state !== 'closed') {
      resend.disabled = false;
    }
  }

  function onChangeEmail() {
    clearInterval(cooldownTimer);
    lockChoices(false);
    saveVotes(getVotes());
    state = 'empty';
    render();
    email.focus();
    email.select();
    captcha.prepare(slots.form);
  }

  document.addEventListener('change', (event) => {
    if (event.target.form !== form || !VOTE_NAME.test(event.target.name)) {
      return;
    }

    saveVotes(getVotes());
    render();

    // Restart the pulse, also when the same card is picked again.
    const card = event.target.checked && event.target.closest('.awards-nominee');

    if (card) {
      card.classList.remove(JUST_PICKED);
      void card.offsetWidth;
      card.classList.add(JUST_PICKED);
      card.addEventListener('animationend', () => card.classList.remove(JUST_PICKED), { once: true });
    }
  });

  // Radios can't be cleared natively: a click or Space on the nominee that is
  // already chosen clears that category. No change event fires then, so send one.
  function clearIfChosen(radio) {
    if (radio.form !== form || !VOTE_NAME.test(radio.name) || chosen[radio.dataset.categoryUid] !== radio.value) {
      return false;
    }

    radio.checked = false;
    radio.dispatchEvent(new Event('change', { bubbles: true }));

    return true;
  }

  document.addEventListener('click', (event) => clearIfChosen(event.target));

  // Space on a checked radio fires no click, unlike a mouse click.
  document.addEventListener('keydown', (event) => {
    if (event.key === ' ' && clearIfChosen(event.target)) {
      event.preventDefault();
    }
  });

  form.addEventListener('submit', onSubmit);

  form.addEventListener('input', (event) => {
    if (event.target === email || event.target === terms) {
      setError(event.target.name, '');
    }
  });

  // Turnstile only loads once someone starts filling in the form.
  fields.addEventListener('focusin', () => captcha.prepare(slots.form), { once: true });

  resend.addEventListener('click', onResend);
  root.querySelector('[data-ballot-change]').addEventListener('click', onChangeEmail);

  document.addEventListener(END_EVENT, (event) => {
    if (event.detail.end === end) {
      close();
    }
  });

  restoreVotes();
  render();

  if (isClosed()) {
    close();
  }
}
